import { useState } from "react";
import { useSeoMeta } from "@unhead/react";
import toast from "react-hot-toast";
import {Plus, Pencil, Trash2, X, Loader2, Database, Briefcase, } from 'lucide-react';
import { useJobs } from "@/hooks/useJobs";
import type { JobInput, JobRecord } from "@/hooks/useJobs";

const MINERALS: JobInput["mineral"][] = [
  "Gold",
  "Copper",
  "Coal",
  "Iron Ore",
  "General",
];
const TYPES: JobInput["type"][] = ["FIFO", "Residential", "Remote"];
const EXPERIENCE: JobInput["experience"][] = [
  "Entry Level",
  "Mid Level",
  "Senior",
  "Supervisor",
];

const emptyForm: JobInput = {
  title: "",
  company: "",
  location: "",
  state: "",
  type: "FIFO",
  mineral: "Gold",
  salary: "",
  salaryNum: 0,
  experience: "Entry Level",
  posted: "Just now",
  tags: [],
  featured: false,
  logo: "",
  description: "",
};

export default function Admin() {
  useSeoMeta({
    title: "Manage Job Listings | MineralJobs Admin",
    description:
      "Admin dashboard to create, edit, and delete mining job listings stored in the MineralJobs database.",
  });

  const { jobs, total, loading, usingFallback, createJob, updateJob, deleteJob } =
    useJobs();

  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState<JobInput>(emptyForm);
  const [tagsInput, setTagsInput] = useState("");
  const [saving, setSaving] = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);

  const openCreate = () => {
    setEditingId(null);
    setForm(emptyForm);
    setTagsInput("");
    setShowForm(true);
  };

  const openEdit = (job: JobRecord) => {
    setEditingId(job._id);
    setForm({
      title: job.title,
      company: job.company,
      location: job.location,
      state: job.state,
      type: job.type,
      mineral: job.mineral,
      salary: job.salary,
      salaryNum: job.salaryNum,
      experience: job.experience,
      posted: job.posted,
      tags: job.tags || [],
      featured: job.featured,
      logo: job.logo,
      description: job.description,
    });
    setTagsInput((job.tags || []).join(", "));
    setShowForm(true);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.title || !form.company || !form.location) {
      toast.error("Title, company and location are required.");
      return;
    }
    setSaving(true);
    const payload: JobInput = {
      ...form,
      salaryNum: Number(form.salaryNum) || 0,
      tags: tagsInput
        .split(",")
        .map((t) => t.trim())
        .filter(Boolean),
    };
    try {
      if (editingId) {
        await updateJob(editingId, payload);
        toast.success("Job listing updated.");
      } else {
        await createJob(payload);
        toast.success("Job listing created.");
      }
      setShowForm(false);
    } catch (err) {
      console.error("Save failed:", err);
      toast.error("Could not save. Admin permission may be required.");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm("Delete this job listing? This cannot be undone."))
      return;
    setDeletingId(id);
    try {
      await deleteJob(id);
      toast.success("Job listing deleted.");
    } catch (err) {
      console.error("Delete failed:", err);
      toast.error("Could not delete. Admin permission may be required.");
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <main>
      <div className="min-h-screen pt-16" style={{ background: "#111827" }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-10">
          {/* Header */}
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
            <div>
              <div className="flex items-center gap-2 mb-2">
                <Database size={18} style={{ color: "#f97316" }} />
                <span
                  className="text-xs uppercase tracking-wider text-gray-500"
                  style={{ fontFamily: "JetBrains Mono, monospace" }}
                >
                  Database Admin
                </span>
              </div>
              <h1
                className="text-3xl sm:text-4xl font-black text-white"
                style={{ fontFamily: "Outfit, sans-serif" }}
              >
                Manage Job Listings
              </h1>
              <p className="text-gray-400 mt-1 text-sm">
                <span
                  style={{
                    fontFamily: "JetBrains Mono, monospace",
                    color: "#f97316",
                  }}
                >
                  {total}
                </span>{" "}
                listings in the database
                {usingFallback ? " (showing sample data — DB empty)" : ""}
              </p>
            </div>
            <button
              onClick={openCreate}
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl font-semibold text-white transition-transform hover:scale-[1.02]"
              style={{ background: "linear-gradient(135deg, #f97316, #ea580c)" }}
            >
              <Plus size={18} /> New Listing
            </button>
          </div>

          {/* Table */}
          {loading ? (
            <div className="flex items-center justify-center py-20 rounded-2xl border border-white/5" style={{ background: "#1a1f2e" }}>
              <Loader2 size={28} className="animate-spin" style={{ color: "#f97316" }} />
              <span className="ml-3 text-gray-400 text-sm">Loading listings...</span>
            </div>
          ) : (
            <div className="overflow-x-auto rounded-2xl border border-white/5" style={{ background: "#1a1f2e" }}>
              <table className="w-full text-left">
                <thead>
                  <tr className="border-b border-white/5">
                    {["Title", "Company", "Mineral", "Type", "Salary", "Actions"].map((h) => (
                      <th
                        key={h}
                        className="px-4 py-3 text-xs uppercase tracking-wider text-gray-500"
                        style={{ fontFamily: "JetBrains Mono, monospace" }}
                      >
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {jobs.map((job) => (
                    <tr key={job._id} className="border-b border-white/5 hover:bg-white/[0.02]">
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <div className="w-8 h-8 rounded-lg flex items-center justify-center text-xs font-bold text-white shrink-0" style={{ background: "rgba(249,115,22,0.15)", color: "#fb923c" }}>
                            {job.logo}
                          </div>
                          <span className="text-white text-sm font-medium">{job.title}</span>
                          {job.featured ? (
                            <span className="text-[10px] px-1.5 py-0.5 rounded-full" style={{ background: "rgba(249,115,22,0.15)", color: "#fb923c" }}>★</span>
                          ) : null}
                        </div>
                      </td>
                      <td className="px-4 py-3 text-gray-400 text-sm">{job.company}</td>
                      <td className="px-4 py-3 text-gray-400 text-sm">{job.mineral}</td>
                      <td className="px-4 py-3 text-gray-400 text-sm">{job.type}</td>
                      <td className="px-4 py-3 text-sm" style={{ fontFamily: "JetBrains Mono, monospace", color: "#fb923c" }}>{job.salary}</td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => openEdit(job)}
                            disabled={usingFallback}
                            title={usingFallback ? "Sample data cannot be edited" : "Edit"}
                            className="p-1.5 rounded-lg text-gray-400 hover:text-orange-400 hover:bg-white/5 transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                          >
                            <Pencil size={15} />
                          </button>
                          <button
                            onClick={() => handleDelete(job._id)}
                            disabled={usingFallback || deletingId === job._id}
                            title={usingFallback ? "Sample data cannot be deleted" : "Delete"}
                            className="p-1.5 rounded-lg text-gray-400 hover:text-red-400 hover:bg-white/5 transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                          >
                            {deletingId === job._id ? (
                              <Loader2 size={15} className="animate-spin" />
                            ) : (
                              <Trash2 size={15} />
                            )}
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                  {jobs.length === 0 ? (
                    <tr>
                      <td colSpan={6} className="px-4 py-12 text-center text-gray-500">
                        <Briefcase size={28} className="mx-auto mb-2 opacity-40" />
                        No job listings yet. Create your first one.
                      </td>
                    </tr>
                  ) : null}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Form Modal */}
      {showForm ? (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4" style={{ background: "rgba(0,0,0,0.7)" }}>
          <div className="w-full max-w-2xl max-h-[90vh] overflow-y-auto rounded-2xl border border-white/10 p-6" style={{ background: "#1a1f2e" }}>
            <div className="flex items-center justify-between mb-5">
              <h2 className="text-xl font-bold text-white" style={{ fontFamily: "Outfit, sans-serif" }}>
                {editingId ? "Edit Listing" : "New Listing"}
              </h2>
              <button onClick={() => setShowForm(false)} className="text-gray-400 hover:text-white p-1">
                <X size={20} />
              </button>
            </div>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <Field label="Job Title *">
                  <input className={inputCls} value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="Surface Drill Operator" />
                </Field>
                <Field label="Company *">
                  <input className={inputCls} value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} placeholder="Newcrest Mining" />
                </Field>
                <Field label="Location *">
                  <input className={inputCls} value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} placeholder="Kalgoorlie, WA" />
                </Field>
                <Field label="State">
                  <input className={inputCls} value={form.state} onChange={(e) => setForm({ ...form, state: e.target.value })} placeholder="WA" />
                </Field>
                <Field label="Mineral">
                  <select className={inputCls} value={form.mineral} onChange={(e) => setForm({ ...form, mineral: e.target.value as JobInput["mineral"] })}>
                    {MINERALS.map((m) => <option key={m} value={m}>{m}</option>)}
                  </select>
                </Field>
                <Field label="Work Type">
                  <select className={inputCls} value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value as JobInput["type"] })}>
                    {TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
                  </select>
                </Field>
                <Field label="Experience">
                  <select className={inputCls} value={form.experience} onChange={(e) => setForm({ ...form, experience: e.target.value as JobInput["experience"] })}>
                    {EXPERIENCE.map((x) => <option key={x} value={x}>{x}</option>)}
                  </select>
                </Field>
                <Field label="Logo (2 letters)">
                  <input className={inputCls} maxLength={3} value={form.logo} onChange={(e) => setForm({ ...form, logo: e.target.value.toUpperCase() })} placeholder="NC" />
                </Field>
                <Field label="Salary (display)">
                  <input className={inputCls} value={form.salary} onChange={(e) => setForm({ ...form, salary: e.target.value })} placeholder="$95,000 - $115,000" />
                </Field>
                <Field label="Salary (number)">
                  <input type="number" className={inputCls} value={form.salaryNum || ""} onChange={(e) => setForm({ ...form, salaryNum: Number(e.target.value) })} placeholder="105000" />
                </Field>
              </div>
              <Field label="Tags (comma separated)">
                <input className={inputCls} value={tagsInput} onChange={(e) => setTagsInput(e.target.value)} placeholder="FIFO, Gold, Drill & Blast, WA" />
              </Field>
              <Field label="Description">
                <textarea className={inputCls} rows={3} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="Operate surface drilling equipment..." />
              </Field>
              <label className="flex items-center gap-2 text-sm text-gray-300 cursor-pointer">
                <input type="checkbox" checked={form.featured} onChange={(e) => setForm({ ...form, featured: e.target.checked })} className="accent-orange-500" />
                Mark as featured listing
              </label>
              <div className="flex items-center gap-3 pt-2">
                <button
                  type="submit"
                  disabled={saving}
                  className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl font-semibold text-white transition-transform hover:scale-[1.02] disabled:opacity-60"
                  style={{ background: "linear-gradient(135deg, #f97316, #ea580c)" }}
                >
                  {saving ? <Loader2 size={16} className="animate-spin" /> : null}
                  {editingId ? "Save Changes" : "Create Listing"}
                </button>
                <button type="button" onClick={() => setShowForm(false)} className="px-5 py-2.5 rounded-xl text-gray-300 hover:text-white hover:bg-white/5 transition-colors">
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      ) : null}
    </main>
  );
}

const inputCls =
  "w-full px-3 py-2 rounded-lg bg-[#111827] border border-white/10 text-white text-sm focus:outline-none focus:border-orange-500/60 placeholder:text-gray-600";

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <label className="block text-xs text-gray-400 mb-1.5" style={{ fontFamily: "JetBrains Mono, monospace" }}>
        {label}
      </label>
      {children}
    </div>
  );
}
